import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
import "./i18n";

import { Layout } from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Spinner } from "./components/ui/Spinner";
import { ToastProvider } from "./components/ui/Toast";
import { AnnouncerProvider } from "./components/a11y/Announcer";
import { ThemeProvider } from "./theme/ThemeProvider";
import { AuthProvider } from "./auth/AuthContext";
import { IdleLogout } from "./auth/IdleLogout";
import { NotFoundPage, RouterErrorPage } from "./pages/errors/ErrorPages";

// ---------------------------------------------------------------------------
//  Lazy page imports — each becomes its own JS chunk so the initial bundle
//  shrinks dramatically. Route-level Suspense fallback shows a Spinner while
//  the page chunk is fetched.
// ---------------------------------------------------------------------------

const LoginPage                  = lazy(() => import("./pages/LoginPage").then(m => ({ default: m.LoginPage })));
const DashboardPage              = lazy(() => import("./pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const SerialOffenderSearchPage   = lazy(() => import("./pages/SerialOffenderSearchPage").then(m => ({ default: m.SerialOffenderSearchPage })));
const FIRListPage                = lazy(() => import("./pages/FIRListPage").then(m => ({ default: m.FIRListPage })));
const MyCasesPage                = lazy(() => import("./pages/MyCasesPage"));
const CaseDashboardPage          = lazy(() => import("./pages/CaseDashboardPage"));
const FIRDetailPage              = lazy(() => import("./pages/FIRDetailPage").then(m => ({ default: m.FIRDetailPage })));
const FIRIntakePage              = lazy(() => import("./pages/FIRIntakePage").then(m => ({ default: m.FIRIntakePage })));
const IncomingTransfersPage      = lazy(() => import("./pages/IncomingTransfersPage").then(m => ({ default: m.IncomingTransfersPage })));
const FIRCompletePage            = lazy(() => import("./pages/FIRCompletePage").then(m => ({ default: m.FIRCompletePage })));
const TemplatesBrowserPage       = lazy(() => import("./pages/TemplatesBrowserPage").then(m => ({ default: m.TemplatesBrowserPage })));
const CitizenPortalPage          = lazy(() => import("./pages/CitizenPortalPage").then(m => ({ default: m.CitizenPortalPage })));

const EvidencePages = () => import("./pages/EvidenceListPage");
const EvidenceListPage     = lazy(() => EvidencePages().then(m => ({ default: m.EvidenceListPage })));
const EvidenceNewPage      = lazy(() => EvidencePages().then(m => ({ default: m.EvidenceNewPage })));
const EvidenceDetailPage   = lazy(() => EvidencePages().then(m => ({ default: m.EvidenceDetailPage })));
const EvidenceTransferPage = lazy(() => EvidencePages().then(m => ({ default: m.EvidenceTransferPage })));

const PancPages = () => import("./pages/PanchanamaPages");
const PanchanamaListPage   = lazy(() => PancPages().then(m => ({ default: m.PanchanamaListPage })));
const PanchanamaNewPage    = lazy(() => PancPages().then(m => ({ default: m.PanchanamaNewPage })));
const PanchanamaDetailPage = lazy(() => PancPages().then(m => ({ default: m.PanchanamaDetailPage })));
const PanchanamaIndexPage  = lazy(() => PancPages().then(m => ({ default: m.PanchanamaIndexPage })));

const StmtPages = () => import("./pages/StatementPages");
const StatementListPage   = lazy(() => StmtPages().then(m => ({ default: m.StatementListPage })));
const StatementNewPage    = lazy(() => StmtPages().then(m => ({ default: m.StatementNewPage })));
const StatementDetailPage = lazy(() => StmtPages().then(m => ({ default: m.StatementDetailPage })));

const DraftPages = () => import("./pages/DraftingPages");
const DraftingWorkbenchPage  = lazy(() => DraftPages().then(m => ({ default: m.DraftingWorkbenchPage })));
const DraftsListPage         = lazy(() => DraftPages().then(m => ({ default: m.DraftsListPage })));
const SupervisorDraftsQueue  = lazy(() => DraftPages().then(m => ({ default: m.SupervisorDraftsQueue })));

const ArrestPages = () => import("./pages/ArrestPages");
const ArrestListPage   = lazy(() => ArrestPages().then(m => ({ default: m.ArrestListPage })));
const ArrestNewPage    = lazy(() => ArrestPages().then(m => ({ default: m.ArrestNewPage })));
const ArrestDetailPage = lazy(() => ArrestPages().then(m => ({ default: m.ArrestDetailPage })));

const CSPages = () => import("./pages/ChargeSheetPages");
const ChargeSheetListPage   = lazy(() => CSPages().then(m => ({ default: m.ChargeSheetListPage })));
const ChargeSheetNewPage    = lazy(() => CSPages().then(m => ({ default: m.ChargeSheetNewPage })));
const ChargeSheetDetailPage = lazy(() => CSPages().then(m => ({ default: m.ChargeSheetDetailPage })));

const SupervisorPage              = lazy(() => import("./pages/SupervisorPage").then(m => ({ default: m.SupervisorPage })));
const ReportsPage                 = lazy(() => import("./pages/ReportsPage").then(m => ({ default: m.ReportsPage })));
const AuditTrailPage              = lazy(() => import("./pages/AuditTrailPage").then(m => ({ default: m.AuditTrailPage })));
const InvestigationDashboardPage  = lazy(() => import("./pages/InvestigationDashboardPage").then(m => ({ default: m.InvestigationDashboardPage })));
const CDRAnalysisPage             = lazy(() => import("./pages/CDRAnalysisPage").then(m => ({ default: m.CDRAnalysisPage })));

const CitizenP = () => import("./pages/CitizenPages");
const PublicComplaintPage = lazy(() => CitizenP().then(m => ({ default: m.PublicComplaintPage })));
const GrievancePage       = lazy(() => CitizenP().then(m => ({ default: m.GrievancePage })));
const RTIPage             = lazy(() => CitizenP().then(m => ({ default: m.RTIPage })));
const TrackByCodePage     = lazy(() => CitizenP().then(m => ({ default: m.TrackByCodePage })));

const AdminP = () => import("./pages/AdminPages");
const AdminOfficersPage     = lazy(() => AdminP().then(m => ({ default: m.AdminOfficersPage })));
const AdminPSPage           = lazy(() => AdminP().then(m => ({ default: m.AdminPSPage })));
const AdminDevicesPage      = lazy(() => AdminP().then(m => ({ default: m.AdminDevicesPage })));
const AdminIntegrationsPage = lazy(() => AdminP().then(m => ({ default: m.AdminIntegrationsPage })));

const InfoP = () => import("./pages/InfoPages");
const WorkflowsPage       = lazy(() => InfoP().then(m => ({ default: m.WorkflowsPage })));
const CrimeWorkflowsPage  = lazy(() => InfoP().then(m => ({ default: m.CrimeWorkflowsPage })));
const SOPsPage            = lazy(() => InfoP().then(m => ({ default: m.SOPsPage })));
const PanchanamaTypesPage = lazy(() => InfoP().then(m => ({ default: m.PanchanamaTypesPage })));
const HardwarePage        = lazy(() => InfoP().then(m => ({ default: m.HardwarePage })));
const RegistersPage       = lazy(() => InfoP().then(m => ({ default: m.RegistersPage })));
const HelpPage            = lazy(() => InfoP().then(m => ({ default: m.HelpPage })));
const SettingsPage        = lazy(() => InfoP().then(m => ({ default: m.SettingsPage })));
const KioskPage           = lazy(() => InfoP().then(m => ({ default: m.KioskPage })));
const AIModulesPage       = lazy(() => InfoP().then(m => ({ default: m.AIModulesPage })));
const FutureFeaturesPage  = lazy(() => InfoP().then(m => ({ default: m.FutureFeaturesPage })));
const FieldKitsPage       = lazy(() => InfoP().then(m => ({ default: m.FieldKitsPage })));
const AdvancedToolsPage   = lazy(() => InfoP().then(m => ({ default: m.AdvancedToolsPage })));
const MigrationMapPage    = lazy(() => InfoP().then(m => ({ default: m.MigrationMapPage })));
const AIPerStepPage       = lazy(() => InfoP().then(m => ({ default: m.AIPerStepPage })));
const MOPatternsPage      = lazy(() => InfoP().then(m => ({ default: m.MOPatternsPage })));
const CourtCompliancePage = lazy(() => InfoP().then(m => ({ default: m.CourtCompliancePage })));
const DocumentLinkingPage = lazy(() => InfoP().then(m => ({ default: m.DocumentLinkingPage })));
const UXInnovationsPage   = lazy(() => InfoP().then(m => ({ default: m.UXInnovationsPage })));

const CasesP = () => import("./pages/CasesPages");
const ComplaintsListPage    = lazy(() => CasesP().then(m => ({ default: m.ComplaintsListPage })));
const ComplaintDetailPage   = lazy(() => CasesP().then(m => ({ default: m.ComplaintDetailPage })));
const ComplaintNewPage      = lazy(() => CasesP().then(m => ({ default: m.ComplaintNewPage })));
const CaseDiaryPage         = lazy(() => CasesP().then(m => ({ default: m.CaseDiaryPage })));
const MLCListPage           = lazy(() => CasesP().then(m => ({ default: m.MLCListPage })));
const MLCNewPage            = lazy(() => CasesP().then(m => ({ default: m.MLCNewPage })));
const MLCDetailPage         = lazy(() => CasesP().then(m => ({ default: m.MLCDetailPage })));
const HospitalMasterPage    = lazy(() => CasesP().then(m => ({ default: m.HospitalMasterPage })));
const FSLListPage           = lazy(() => CasesP().then(m => ({ default: m.FSLListPage })));
const FSLNewPage            = lazy(() => CasesP().then(m => ({ default: m.FSLNewPage })));
const FSLDetailPage         = lazy(() => CasesP().then(m => ({ default: m.FSLDetailPage })));
const FslLabMasterPage      = lazy(() => CasesP().then(m => ({ default: m.FslLabMasterPage })));
const MalkhanaPage          = lazy(() => CasesP().then(m => ({ default: m.MalkhanaPage })));
const CourtPairviPage       = lazy(() => CasesP().then(m => ({ default: m.CourtPairviPage })));
const CourtMasterPage       = lazy(() => CasesP().then(m => ({ default: m.CourtMasterPage })));
const MyCaseDiaryPage       = lazy(() => CasesP().then(m => ({ default: m.MyCaseDiaryPage })));
const CourtFilingsListPage  = lazy(() => CasesP().then(m => ({ default: m.CourtFilingsListPage })));
const CourtFilingDetailPage = lazy(() => CasesP().then(m => ({ default: m.CourtFilingDetailPage })));

const AIOSahayakPage          = lazy(() => import("./pages/AIOSahayakPage").then(m => ({ default: m.AIOSahayakPage })));
const AIOSahayakConfigPage    = lazy(() => import("./pages/AIOSahayakConfigPage").then(m => ({ default: m.AIOSahayakConfigPage })));
const VoiceComplaintBotPage   = lazy(() => import("./pages/VoiceComplaintBotPage").then(m => ({ default: m.VoiceComplaintBotPage })));

const RegistersP = () => import("./pages/RegistersPages");
const StationDiaryPage    = lazy(() => RegistersP().then(m => ({ default: m.StationDiaryPage })));
const MissingPersonsPage  = lazy(() => RegistersP().then(m => ({ default: m.MissingPersonsPage })));
const LostFoundPage       = lazy(() => RegistersP().then(m => ({ default: m.LostFoundPage })));
const WantedPage          = lazy(() => RegistersP().then(m => ({ default: m.WantedPage })));
const ProcessDocsPage     = lazy(() => RegistersP().then(m => ({ default: m.ProcessDocsPage })));
const ProcessDocDetailPage = lazy(() => RegistersP().then(m => ({ default: m.ProcessDocDetailPage })));
const BailRegisterPage    = lazy(() => RegistersP().then(m => ({ default: m.BailRegisterPage })));
const VerificationPage    = lazy(() => RegistersP().then(m => ({ default: m.VerificationPage })));
const OfficerProfilePage  = lazy(() => RegistersP().then(m => ({ default: m.OfficerProfilePage })));
const NCRPage             = lazy(() => RegistersP().then(m => ({ default: m.NCRPage })));

const SectionMatrixPage      = lazy(() => import("./pages/SectionMatrixPage").then(m => ({ default: m.SectionMatrixPage })));
const SOSPage                = lazy(() => import("./pages/SOSPage").then(m => ({ default: m.SOSPage })));
const MOPatternPage          = lazy(() => import("./pages/MOPatternPage").then(m => ({ default: m.MOPatternPage })));
const NCRDetailPage          = lazy(() => import("./pages/NCRDetailPage").then(m => ({ default: m.NCRDetailPage })));
const MissingPersonDetailPage = lazy(() => import("./pages/MissingPersonDetailPage").then(m => ({ default: m.MissingPersonDetailPage })));
const LostFoundDetailPage    = lazy(() => import("./pages/LostFoundDetailPage").then(m => ({ default: m.LostFoundDetailPage })));
const WantedDetailPage       = lazy(() => import("./pages/WantedDetailPage").then(m => ({ default: m.WantedDetailPage })));
const VerifyDetailPage       = lazy(() => import("./pages/VerifyDetailPage").then(m => ({ default: m.VerifyDetailPage })));
const BailDetailPage         = lazy(() => import("./pages/BailDetailPage").then(m => ({ default: m.BailDetailPage })));
const ToolCapturesPage       = lazy(() => import("./pages/ToolCapturesPage").then(m => ({ default: m.ToolCapturesPage })));
const IntegrationsHubPage    = lazy(() => import("./pages/IntegrationsHubPage").then(m => ({ default: m.IntegrationsHubPage })));
const MahaLLMPage            = lazy(() => import("./pages/MahaLLMPage").then(m => ({ default: m.MahaLLMPage })));

const MoreInfoP = () => import("./pages/MoreInfoPages");
const AlertsPage          = lazy(() => MoreInfoP().then(m => ({ default: m.AlertsPage })));
const SystemAuditLogsPage = lazy(() => MoreInfoP().then(m => ({ default: m.SystemAuditLogsPage })));
const ConfigsPage         = lazy(() => MoreInfoP().then(m => ({ default: m.ConfigsPage })));
const RolesPage           = lazy(() => MoreInfoP().then(m => ({ default: m.RolesPage })));
const SearchPage          = lazy(() => MoreInfoP().then(m => ({ default: m.SearchPage })));
const MapPage             = lazy(() => MoreInfoP().then(m => ({ default: m.MapPage })));

const AadhaarFlowPage         = lazy(() => import("./pages/AadhaarFlowPage").then(m => ({ default: m.AadhaarFlowPage })));
const TemplateFillPage        = lazy(() => import("./pages/TemplateFillPage").then(m => ({ default: m.TemplateFillPage })));
const SystemFlowPage          = lazy(() => import("./pages/SystemFlowPage").then(m => ({ default: m.SystemFlowPage })));
const AIConfigPage            = lazy(() => import("./pages/AIConfigPage").then(m => ({ default: m.AIConfigPage })));
const GrievanceDetailPage     = lazy(() => import("./pages/GrievanceDetailPage").then(m => ({ default: m.GrievanceDetailPage })));
const RTIDetailPage           = lazy(() => import("./pages/RTIDetailPage").then(m => ({ default: m.RTIDetailPage })));
const VisitorDeskPage         = lazy(() => import("./pages/VisitorDeskPage").then(m => ({ default: m.VisitorDeskPage })));
const VisitorJourneyPage      = lazy(() => import("./pages/VisitorJourneyPage").then(m => ({ default: m.VisitorJourneyPage })));
const VisitorQueuePage        = lazy(() => import("./pages/VisitorQueuePage").then(m => ({ default: m.VisitorQueuePage })));
const LockupRegisterPage      = lazy(() => import("./pages/LockupRegisterPage").then(m => ({ default: m.LockupRegisterPage })));
const LockupDetailPage        = lazy(() => import("./pages/LockupDetailPage").then(m => ({ default: m.LockupDetailPage })));
const CustodyPage             = lazy(() => import("./pages/CustodyPage").then(m => ({ default: m.CustodyPage })));
const CourtCalendarPage       = lazy(() => import("./pages/CourtCalendarPage"));
const DutyRosterPage          = lazy(() => import("./pages/DutyRosterPage"));
const PCRDeploymentPage       = lazy(() => import("./pages/PCRDeploymentPage"));
const UDRPage                 = lazy(() => import("./pages/UDRPage"));
const UDRDetailPage           = lazy(() => import("./pages/UDRDetailPage"));
const ArmsRegisterPage        = lazy(() => import("./pages/ArmsRegisterPage"));
const PDRegisterPage          = lazy(() => import("./pages/PDRegisterPage"));
const TARPage                 = lazy(() => import("./pages/TARPage"));
const BandobastPage           = lazy(() => import("./pages/BandobastPage"));
const GDPage                  = lazy(() => import("./pages/GDPage"));
const MissingPersonsRegisterPage = lazy(() => import("./pages/MissingPersonsPage"));
const WarrantPage             = lazy(() => import("./pages/WarrantPage"));
const ArrestPage              = lazy(() => import("./pages/ArrestPage"));
const NCPage                  = lazy(() => import("./pages/NCPage"));
const HistorySheetersPage     = lazy(() => import("./pages/HistorySheetersPage"));
const SummonsPage             = lazy(() => import("./pages/SummonsPage"));
const AccidentalDeathPage     = lazy(() => import("./pages/AccidentalDeathPage"));
const ChallanPage             = lazy(() => import("./pages/ChallanPage"));

const NotificationsPage       = lazy(() => import("./pages/NotificationsPage"));
const ContradictionsPage      = lazy(() => import("./pages/ContradictionsPage"));
const CaseFilesP = () => import("./pages/CaseFilePages");
const CaseFilesListPage       = lazy(() => CaseFilesP().then(m => ({ default: m.CaseFilesListPage })));
const CaseFileDetailPage      = lazy(() => CaseFilesP().then(m => ({ default: m.CaseFileDetailPage })));
const DedupInboxPage          = lazy(() => import("./pages/DedupInboxPage"));
const CourtBundleP = () => import("./pages/CourtBundlePages");
const CourtBundleListPage     = lazy(() => CourtBundleP().then(m => ({ default: m.CourtBundleListPage })));
const CourtBundleDetailPage   = lazy(() => CourtBundleP().then(m => ({ default: m.CourtBundleDetailPage })));
const AnalyticsPage           = lazy(() => import("./pages/AnalyticsPage"));
const WarrantDetailPage       = lazy(() => import("./pages/WarrantDetailPage"));
const PreFilingCheckPage      = lazy(() => import("./pages/PreFilingCheckPage"));
const MalkhanaAIP = () => import("./pages/MalkhanaAIPages");
const MalkhanaExtractsPage    = lazy(() => MalkhanaAIP().then(m => ({ default: m.MalkhanaExtractsPage })));
const MalkhanaAnomaliesPage   = lazy(() => MalkhanaAIP().then(m => ({ default: m.MalkhanaAnomaliesPage })));
const EmbedSearchPage         = lazy(() => import("./pages/EmbedSearchPage"));
const AdminExtP = () => import("./pages/AdminExtPages");
const AdminChainHeadsPage     = lazy(() => AdminExtP().then(m => ({ default: m.AdminChainHeadsPage })));
const AdminVoiceSessionsPage  = lazy(() => AdminExtP().then(m => ({ default: m.AdminVoiceSessionsPage })));
const PublicPortalP = () => import("./pages/PublicPortalPages");
const PublicVerificationStatusPage = lazy(() => PublicPortalP().then(m => ({ default: m.PublicVerificationStatusPage })));
const PublicFIRCopyPage       = lazy(() => PublicPortalP().then(m => ({ default: m.PublicFIRCopyPage })));
const PublicIIFInfoPage       = lazy(() => PublicPortalP().then(m => ({ default: m.PublicIIFInfoPage })));

const NoticeP = () => import("./pages/NoticePages");
const NoticeS35ListPage    = lazy(() => NoticeP().then(m => ({ default: m.NoticeS35ListPage })));
const NoticeS35NewPage     = lazy(() => NoticeP().then(m => ({ default: m.NoticeS35NewPage })));
const NoticeS35DetailPage  = lazy(() => NoticeP().then(m => ({ default: m.NoticeS35DetailPage })));
const NoticeS41ANewPage    = lazy(() => NoticeP().then(m => ({ default: m.NoticeS41ANewPage })));
const NoticeS175NewPage    = lazy(() => NoticeP().then(m => ({ default: m.NoticeS175NewPage })));

const InvRegP = () => import("./pages/InvestigationRegisterPages");
const InvestigationRegisterListPage   = lazy(() => InvRegP().then(m => ({ default: m.InvestigationRegisterListPage })));
const InvestigationRegisterDetailPage = lazy(() => InvRegP().then(m => ({ default: m.InvestigationRegisterDetailPage })));
const PreliminaryInquiryPage          = lazy(() => InvRegP().then(m => ({ default: m.PreliminaryInquiryPage })));
const ZeroFIRTransferPage             = lazy(() => InvRegP().then(m => ({ default: m.ZeroFIRTransferPage })));

const TIPP = () => import("./pages/TIPPages");
const TIPListPage   = lazy(() => TIPP().then(m => ({ default: m.TIPListPage })));
const TIPDetailPage = lazy(() => TIPP().then(m => ({ default: m.TIPDetailPage })));

const ForP = () => import("./pages/ForensicsPages");
const FSLMemoNewPage    = lazy(() => ForP().then(m => ({ default: m.FSLMemoNewPage })));
const FSLMemoDetailPage = lazy(() => ForP().then(m => ({ default: m.FSLMemoDetailPage })));
const MLCPurposePanel   = lazy(() => ForP().then(m => ({ default: m.MLCPurposePanel })));

const CSExtraP = () => import("./pages/ChargeSheetExtrasPages");
const ChargeSheetWorkbenchPage = lazy(() => CSExtraP().then(m => ({ default: m.ChargeSheetWorkbenchPage })));
const BilingualCheckPage       = lazy(() => CSExtraP().then(m => ({ default: m.BilingualCheckPage })));

const CaseDiaryRegisterPage    = lazy(() => import("./pages/CaseDiaryRegisterPage").then(m => ({ default: m.CaseDiaryRegisterPage })));
const PanchanamaSchemaPage     = lazy(() => import("./pages/PanchanamaSchemaPage").then(m => ({ default: m.PanchanamaSchemaPage })));

const ClosureP = () => import("./pages/ClosureRegisterPages");
const ClosureNewPage      = lazy(() => ClosureP().then(m => ({ default: m.ClosureNewPage })));
const ClosureDetailPage   = lazy(() => ClosureP().then(m => ({ default: m.ClosureDetailPage })));
const ClosureOverviewPage = lazy(() => ClosureP().then(m => ({ default: m.ClosureOverviewPage })));

const CFExtraP = () => import("./pages/CourtFilingExtrasPages");
const CourtFilingBundlesPage     = lazy(() => CFExtraP().then(m => ({ default: m.CourtFilingBundlesPage })));
const PropertyOrderNewPage       = lazy(() => CFExtraP().then(m => ({ default: m.PropertyOrderNewPage })));
const CompensationOrderNewPage   = lazy(() => CFExtraP().then(m => ({ default: m.CompensationOrderNewPage })));
const AppealNewPage              = lazy(() => CFExtraP().then(m => ({ default: m.AppealNewPage })));
const AppealDetailPage           = lazy(() => CFExtraP().then(m => ({ default: m.AppealDetailPage })));
const FilingAppealsPage          = lazy(() => CFExtraP().then(m => ({ default: m.FilingAppealsPage })));

const DDP = () => import("./pages/DyingDeclarationPage");
const DyingDeclarationListPage   = lazy(() => DDP().then(m => ({ default: m.DyingDeclarationListPage })));
const DyingDeclarationDetailPage = lazy(() => DDP().then(m => ({ default: m.DyingDeclarationDetailPage })));

const PCRJobsPage              = lazy(() => import("./pages/PCRJobsPage").then(m => ({ default: m.PCRJobsPage })));
const ConvertComplaintToNCRPage = lazy(() => import("./pages/ConvertComplaintToNCRPage").then(m => ({ default: m.ConvertComplaintToNCRPage })));

const CCTNSP = () => import("./pages/CCTNSIIFPages");
const CCTNSIIFHubPage        = lazy(() => CCTNSP().then(m => ({ default: m.CCTNSIIFHubPage })));
const IIF2BuilderPage        = lazy(() => CCTNSP().then(m => ({ default: m.IIF2BuilderPage })));
const IIFDriftDashboardPage  = lazy(() => CCTNSP().then(m => ({ default: m.IIFDriftDashboardPage })));

const IIFFormP = () => import("./pages/IIFFormPages");
const IIF1FIRFormPage         = lazy(() => IIFFormP().then(m => ({ default: m.IIF1FIRFormPage })));
const IIF2FormPage            = lazy(() => IIFFormP().then(m => ({ default: m.IIF2FormPage })));
const IIF3ArrestFormPage      = lazy(() => IIFFormP().then(m => ({ default: m.IIF3ArrestFormPage })));
const IIF4ChargeSheetFormPage = lazy(() => IIFFormP().then(m => ({ default: m.IIF4ChargeSheetFormPage })));
const IIF5CourtDisposalFormPage = lazy(() => IIFFormP().then(m => ({ default: m.IIF5CourtDisposalFormPage })));
const IIF6AppealResultFormPage = lazy(() => IIFFormP().then(m => ({ default: m.IIF6AppealResultFormPage })));
const IIF7PanchanamaFormPage  = lazy(() => IIFFormP().then(m => ({ default: m.IIF7PanchanamaFormPage })));

const MobIIFP = () => import("./pages/MobileIIFPages");
const MobileIIFHubPage    = lazy(() => MobIIFP().then(m => ({ default: m.MobileIIFHubPage })));
const MobileIIFViewerPage = lazy(() => MobIIFP().then(m => ({ default: m.MobileIIFViewerPage })));

const AdminIIFPage = lazy(() => import("./pages/AdminIIFPage").then(m => ({ default: m.AdminIIFPage })));

// MeriPehchaan / DigiLocker / e-Sign (Phases 1–3)
const MeriPehchaanCallbackPage = lazy(() => import("./pages/MeriPehchaanCallbackPage").then(m => ({ default: m.MeriPehchaanCallbackPage })));
const DigiLockerCallbackPage = lazy(() => import("./pages/DigiLockerCallbackPage").then(m => ({ default: m.DigiLockerCallbackPage })));
const AdminCitizenConsentsPage = lazy(() => import("./pages/AdminCitizenConsentsPage").then(m => ({ default: m.AdminCitizenConsentsPage })));

// ---------------------------------------------------------------------------
//  Query client
// ---------------------------------------------------------------------------

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err: unknown) => {
        // No retry on 4xx — those are not transient.
        const status = (err as { status?: number } | null)?.status;
        if (status && status >= 400 && status < 500) return false;
        return count < 2;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

// ---------------------------------------------------------------------------
//  Legacy auth-token bootstrap (keeps existing LoginPage working until the
//  backend ships cookie-based auth). Cookie path is the future.
// ---------------------------------------------------------------------------
try {
  const raw = sessionStorage.getItem("mhpol.auth");
  if (raw) {
    const { token, ps } = JSON.parse(raw);
    if (token) {
      import("./lib/api").then(({ setAuthToken }) => setAuthToken(token, ps || null));
    }
  }
} catch { /* sessionStorage may throw in iframe strict mode */ }

// ---------------------------------------------------------------------------
//  Suspense fallback used by every route
// ---------------------------------------------------------------------------
function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-muted">
      <Spinner size="lg" />
    </div>
  );
}

// ---------------------------------------------------------------------------
//  App composition
// ---------------------------------------------------------------------------

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ThemeProvider>
      <ErrorBoundary>
        <QueryClientProvider client={qc}>
          <AnnouncerProvider>
            <ToastProvider>
              <AuthProvider>
                <IdleLogout idleMs={15 * 60_000} warningMs={60_000} />
                <BrowserRouter>
                  <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      <Route path="/login"  element={<LoginPage />} errorElement={<RouterErrorPage />} />
                      <Route path="/kiosk"  element={<KioskPage />} />
                      <Route path="/citizen/file"            element={<PublicComplaintPage />} />
                      <Route path="/citizen/grievance"       element={<GrievancePage />} />
                      <Route path="/citizen/rti"             element={<RTIPage />} />
                      <Route path="/citizen/track"           element={<TrackByCodePage />} />
                      <Route path="/citizen/verify/:case_no" element={<PublicVerificationStatusPage />} />
                      <Route path="/citizen/fir-copy"        element={<PublicFIRCopyPage />} />
                      <Route path="/citizen/iif-info"        element={<PublicIIFInfoPage />} />

                      {/* OIDC / OAuth2 callbacks (public — pre-auth) */}
                      <Route path="/oauth/meripehchaan/callback" element={<MeriPehchaanCallbackPage />} />
                      <Route path="/oauth/digilocker/callback"   element={<DigiLockerCallbackPage />} />

                      <Route path="/" element={<Layout />} errorElement={<RouterErrorPage />}>
                        <Route index element={<Navigate to="/dashboard" replace />} />
                        <Route path="dashboard" element={<DashboardPage />} />
                        <Route path="serial-search" element={<SerialOffenderSearchPage />} />

                        <Route path="my-cases"    element={<MyCasesPage />} />
                        <Route path="cases/:id"   element={<CaseDashboardPage />} />
                        <Route path="firs"        element={<FIRListPage />} />
                        <Route path="firs/:id"      element={<FIRDetailPage />} />
                        <Route path="firs/:id/edit" element={<FIRCompletePage />} />
                        <Route path="transfers/incoming" element={<IncomingTransfersPage />} />
                        <Route path="fir/new"       element={<FIRIntakePage />} />
                        <Route path="aio-sahayak" element={<AIOSahayakPage />} />

                        <Route path="firs/:fir_id/statements"     element={<StatementListPage />} />
                        <Route path="firs/:fir_id/statements/new" element={<StatementNewPage />} />
                        <Route path="statements/:id"              element={<StatementDetailPage />} />

                        <Route path="firs/:fir_id/evidence"     element={<EvidenceListPage />} />
                        <Route path="firs/:fir_id/evidence/new" element={<EvidenceNewPage />} />
                        <Route path="evidence/:id"              element={<EvidenceDetailPage />} />
                        <Route path="evidence/:id/transfer"     element={<EvidenceTransferPage />} />

                        <Route path="panchanamas"                  element={<PanchanamaIndexPage />} />
                        <Route path="firs/:fir_id/panchanamas"     element={<PanchanamaListPage />} />
                        <Route path="firs/:fir_id/panchanamas/new" element={<PanchanamaNewPage />} />
                        <Route path="panchanamas/:id"              element={<PanchanamaDetailPage />} />

                        <Route path="arrests"                       element={<ArrestListPage />} />
                        <Route path="arrests/new"                   element={<ArrestNewPage />} />
                        <Route path="arrests/:id"                   element={<ArrestDetailPage />} />
                        <Route path="firs/:fir_id/arrests/new"      element={<ArrestNewPage />} />
                        <Route path="firs/:fir_id/arrests/:id"      element={<ArrestDetailPage />} />

                        <Route path="charge-sheets"      element={<ChargeSheetListPage />} />
                        <Route path="charge-sheets/new"  element={<ChargeSheetNewPage />} />
                        <Route path="charge-sheets/:id"  element={<ChargeSheetDetailPage />} />

                        <Route path="firs/:fir_id/investigation" element={<InvestigationDashboardPage />} />
                        <Route path="firs/:fir_id/matrix"        element={<SectionMatrixPage />} />
                        <Route path="firs/:fir_id/mo-pattern"    element={<MOPatternPage />} />
                        <Route path="firs/:fir_id/cdr"           element={<CDRAnalysisPage />} />
                        <Route path="sos"                        element={<SOSPage />} />
                        <Route path="ncr"                        element={<NCRPage />} />
                        <Route path="registers/ncr/:id"          element={<NCRDetailPage />} />
                        <Route path="registers/missing/:id"      element={<MissingPersonDetailPage />} />
                        <Route path="registers/lost-found/:id"   element={<LostFoundDetailPage />} />
                        <Route path="tools/captures"             element={<ToolCapturesPage />} />
                        <Route path="integrations"               element={<IntegrationsHubPage />} />
                        <Route path="mahallm"                    element={<MahaLLMPage />} />
                        <Route path="supervisor"  element={<SupervisorPage />} />
                        <Route path="reports"     element={<ReportsPage />} />
                        <Route path="audit-trail" element={<AuditTrailPage />} />

                        <Route path="templates" element={<TemplatesBrowserPage />} />
                        <Route path="citizen"   element={<CitizenPortalPage />} />

                        <Route path="admin/officers"     element={<AdminOfficersPage />} />
                        <Route path="admin/ps"           element={<AdminPSPage />} />
                        <Route path="admin/devices"      element={<AdminDevicesPage />} />
                        <Route path="admin/integrations" element={<AdminIntegrationsPage />} />

                        <Route path="workflows"        element={<WorkflowsPage />} />
                        <Route path="crime-workflows"  element={<CrimeWorkflowsPage />} />
                        <Route path="sops"             element={<SOPsPage />} />
                        <Route path="panchanama-types" element={<PanchanamaTypesPage />} />
                        <Route path="hardware"         element={<HardwarePage />} />
                        <Route path="ai-modules"       element={<AIModulesPage />} />
                        <Route path="future-features"  element={<FutureFeaturesPage />} />
                        <Route path="field-kits"       element={<FieldKitsPage />} />
                        <Route path="advanced-tools"   element={<AdvancedToolsPage />} />
                        <Route path="migration-map"    element={<MigrationMapPage />} />
                        <Route path="ai-per-step"      element={<AIPerStepPage />} />
                        <Route path="mo-patterns"      element={<MOPatternsPage />} />
                        <Route path="court-compliance" element={<CourtCompliancePage />} />
                        <Route path="document-linking" element={<DocumentLinkingPage />} />
                        <Route path="ux-innovations"   element={<UXInnovationsPage />} />
                        <Route path="registers"        element={<RegistersPage />} />
                        <Route path="help"             element={<HelpPage />} />
                        <Route path="settings"         element={<SettingsPage />} />

                        <Route path="complaints"               element={<ComplaintsListPage />} />
                        <Route path="complaints/new"           element={<ComplaintNewPage />} />
                        <Route path="complaints/voice"         element={<VoiceComplaintBotPage />} />
                        <Route path="complaints/:id"           element={<ComplaintDetailPage />} />
                        <Route path="firs/:fir_id/diary"       element={<CaseDiaryPage />} />
                        <Route path="case-diary"               element={<MyCaseDiaryPage />} />
                        <Route path="firs/:fir_id/drafts"        element={<DraftsListPage />} />
                        <Route path="firs/:fir_id/draft/:kind"   element={<DraftingWorkbenchPage />} />
                        <Route path="admin/drafts"               element={<SupervisorDraftsQueue />} />
                        <Route path="medico-legal"             element={<MLCListPage />} />
                        <Route path="medico-legal/new"         element={<MLCNewPage />} />
                        <Route path="medico-legal/:mlc_id"     element={<MLCDetailPage />} />
                        <Route path="admin/hospitals"          element={<HospitalMasterPage />} />
                        <Route path="forensic"                 element={<FSLListPage />} />
                        <Route path="forensic/new"             element={<FSLNewPage />} />
                        <Route path="forensic/:fsl_id"         element={<FSLDetailPage />} />
                        <Route path="admin/fsl-labs"           element={<FslLabMasterPage />} />
                        <Route path="malkhana"                 element={<MalkhanaPage />} />
                        <Route path="court/filings"            element={<CourtFilingsListPage />} />
                        <Route path="court/filings/:id"        element={<CourtFilingDetailPage />} />
                        <Route path="court/pairvi"             element={<CourtPairviPage />} />
                        <Route path="admin/courts"             element={<CourtMasterPage />} />

                        <Route path="visitor"           element={<VisitorQueuePage />} />
                        <Route path="visitor/new"       element={<VisitorDeskPage />} />
                        <Route path="visitor/:id"       element={<VisitorJourneyPage />} />

                        <Route path="station-diary"            element={<StationDiaryPage />} />
                        <Route path="missing-persons"          element={<MissingPersonsPage />} />
                        <Route path="lost-found"               element={<LostFoundPage />} />
                        <Route path="wanted"                   element={<WantedPage />} />
                        <Route path="wanted/:id"               element={<WantedDetailPage />} />
                        <Route path="process-docs"             element={<ProcessDocsPage />} />
                        <Route path="process-docs/:id"         element={<ProcessDocDetailPage />} />
                        <Route path="bail"                     element={<BailRegisterPage />} />
                        <Route path="bail/:id"                 element={<BailDetailPage />} />
                        <Route path="verification"             element={<VerificationPage />} />
                        <Route path="verify/:id"               element={<VerifyDetailPage />} />
                        <Route path="me"                       element={<OfficerProfilePage />} />

                        <Route path="alerts"                   element={<AlertsPage />} />
                        <Route path="search"                   element={<SearchPage />} />
                        <Route path="map"                      element={<MapPage />} />
                        <Route path="admin/audit-logs"         element={<SystemAuditLogsPage />} />
                        <Route path="admin/configs"            element={<ConfigsPage />} />
                        <Route path="admin/roles"              element={<RolesPage />} />

                        <Route path="identity/aadhaar"         element={<AadhaarFlowPage />} />
                        <Route path="templates/:code/fill"     element={<TemplateFillPage />} />
                        <Route path="flow"                     element={<SystemFlowPage />} />
                        <Route path="admin/ai-config"              element={<AIConfigPage />} />
                        <Route path="admin/ai-config/aio-sahayak"  element={<AIOSahayakConfigPage />} />

                        <Route path="registers/grievances"     element={<GrievancePage />} />
                        <Route path="registers/grievances/:id" element={<GrievanceDetailPage />} />
                        <Route path="registers/rti"            element={<RTIPage />} />
                        <Route path="registers/rti/:id"        element={<RTIDetailPage />} />

                        <Route path="lockup"       element={<LockupRegisterPage />} />
                        <Route path="lockup/:eid"  element={<LockupDetailPage />} />
                        <Route path="custody"      element={<CustodyPage />} />

                        <Route path="court/calendar" element={<CourtCalendarPage />} />
                        <Route path="duty-roster" element={<DutyRosterPage />} />
                        <Route path="pcr" element={<PCRDeploymentPage />} />
                        <Route path="udr" element={<UDRPage />} />
                        <Route path="udr/:uid" element={<UDRDetailPage />} />
                        <Route path="arms" element={<ArmsRegisterPage />} />
                        <Route path="pd" element={<PDRegisterPage />} />
                        <Route path="tar" element={<TARPage />} />
                        <Route path="bandobast" element={<BandobastPage />} />
                        <Route path="gd" element={<GDPage />} />
                        <Route path="missing" element={<MissingPersonsRegisterPage />} />
                        <Route path="warrants" element={<WarrantPage />} />
                        <Route path="arrest-reg" element={<ArrestPage />} />
                        <Route path="nc" element={<NCPage />} />
                        <Route path="history-sheeters" element={<HistorySheetersPage />} />
                        <Route path="summons-reg" element={<SummonsPage />} />
                        <Route path="accidental-deaths" element={<AccidentalDeathPage />} />
                        <Route path="challan-reg" element={<ChallanPage />} />

                        <Route path="admin/notifications"   element={<NotificationsPage />} />
                        <Route path="firs/:fir_id/contradictions" element={<ContradictionsPage />} />
                        <Route path="case-files"            element={<CaseFilesListPage />} />
                        <Route path="case-files/:id"        element={<CaseFileDetailPage />} />
                        <Route path="dedup"                 element={<DedupInboxPage />} />
                        <Route path="court/bundles"         element={<CourtBundleListPage />} />
                        <Route path="court/bundles/:id"     element={<CourtBundleDetailPage />} />
                        <Route path="analytics"             element={<AnalyticsPage />} />
                        <Route path="warrants/:id"          element={<WarrantDetailPage />} />
                        <Route path="charge-sheets/:id/pre-filing" element={<PreFilingCheckPage />} />
                        <Route path="malkhana/extracts"     element={<MalkhanaExtractsPage />} />
                        <Route path="malkhana/anomalies"    element={<MalkhanaAnomaliesPage />} />
                        <Route path="tools/semantic-search" element={<EmbedSearchPage />} />
                        <Route path="admin/chain-heads"     element={<AdminChainHeadsPage />} />
                        <Route path="admin/voice-sessions"  element={<AdminVoiceSessionsPage />} />

                        <Route path="notices/s35"           element={<NoticeS35ListPage />} />
                        <Route path="notices/s35/new"       element={<NoticeS35NewPage />} />
                        <Route path="notices/s35/:id"       element={<NoticeS35DetailPage />} />
                        <Route path="notices/s41a/new"      element={<NoticeS41ANewPage />} />
                        <Route path="notices/s175/new"      element={<NoticeS175NewPage />} />

                        <Route path="inv-register"          element={<InvestigationRegisterListPage />} />
                        <Route path="inv-register/:id"      element={<InvestigationRegisterDetailPage />} />
                        <Route path="preliminary-inquiry"   element={<PreliminaryInquiryPage />} />
                        <Route path="zero-fir-transfer"     element={<ZeroFIRTransferPage />} />

                        <Route path="firs/:fir_id/tip"      element={<TIPListPage />} />
                        <Route path="tip/:id"               element={<TIPDetailPage />} />

                        <Route path="fsl-memos/new"         element={<FSLMemoNewPage />} />
                        <Route path="fsl-memos/:id"         element={<FSLMemoDetailPage />} />
                        <Route path="firs/:fir_id/mlc/:mlc_id/purpose" element={<MLCPurposePanel />} />

                        <Route path="charge-sheets/:id/workbench" element={<ChargeSheetWorkbenchPage />} />
                        <Route path="tools/bilingual-check" element={<BilingualCheckPage />} />

                        <Route path="firs/:fir_id/case-diary-register" element={<CaseDiaryRegisterPage />} />
                        <Route path="panchanama-schemas"    element={<PanchanamaSchemaPage />} />

                        <Route path="closure/new"           element={<ClosureNewPage />} />
                        <Route path="closure/:id"           element={<ClosureDetailPage />} />
                        <Route path="firs/:fir_id/closure-overview" element={<ClosureOverviewPage />} />

                        <Route path="court-filings/:cf_id/bundles" element={<CourtFilingBundlesPage />} />
                        <Route path="property-orders/new"   element={<PropertyOrderNewPage />} />
                        <Route path="compensation-orders/new" element={<CompensationOrderNewPage />} />
                        <Route path="appeals/new"           element={<AppealNewPage />} />
                        <Route path="appeals/:id"           element={<AppealDetailPage />} />
                        <Route path="court-filings/:cf_id/appeals" element={<FilingAppealsPage />} />

                        <Route path="firs/:fir_id/dying-declarations" element={<DyingDeclarationListPage />} />
                        <Route path="dying-declaration/:id" element={<DyingDeclarationDetailPage />} />

                        <Route path="admin/pcr-jobs"        element={<PCRJobsPage />} />
                        <Route path="complaints/:id/convert-to-ncr" element={<ConvertComplaintToNCRPage />} />

                        <Route path="cctns/iif"             element={<CCTNSIIFHubPage />} />
                        <Route path="iif2/:fir_id"          element={<IIF2BuilderPage />} />
                        <Route path="iif/drift"             element={<IIFDriftDashboardPage />} />

                        <Route path="iif/1/:fir_id"           element={<IIF1FIRFormPage />} />
                        <Route path="iif/2/:fir_id"           element={<IIF2FormPage />} />
                        <Route path="iif/3/:arrest_id"        element={<IIF3ArrestFormPage />} />
                        <Route path="iif/4/:cs_id"            element={<IIF4ChargeSheetFormPage />} />
                        <Route path="iif/5/:cf_id"            element={<IIF5CourtDisposalFormPage />} />
                        <Route path="iif/6/:appeal_id"        element={<IIF6AppealResultFormPage />} />
                        <Route path="iif/7/:panchanama_id"    element={<IIF7PanchanamaFormPage />} />

                        <Route path="m/iif"                   element={<MobileIIFHubPage />} />
                        <Route path="m/iif/:form/:id"         element={<MobileIIFViewerPage />} />

                        <Route path="admin/iif"               element={<AdminIIFPage />} />
                        <Route path="admin/citizen-consents" element={<AdminCitizenConsentsPage />} />
                      </Route>
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
              </AuthProvider>
            </ToastProvider>
          </AnnouncerProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </React.StrictMode>,
);
